import { useState, useEffect, useMemo } from 'react';
import CommonConfig from '../../utils/constant';
import Loader from '../Shared/Loader/Loader';
import APIConstant from '../../utils/pathConstants';
import api from '../../utils/apiClient';
import { useCatchResponse } from '../../utils/useCatchResponse';
import '../../assets/css/teacherDashboard.css';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
};

const getTodayDay = () => {
    const todayName = dayNames[new Date().getDay()];
    return days.includes(todayName) ? todayName : 'Monday';
};

const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    // Handle "hh:mm AM/PM" format
    const parts = timeStr.trim().split(' ');
    const [h, m] = parts[0].split(':').map(Number);
    const isPM = parts[1]?.toUpperCase() === 'PM';
    let hours = h;
    if (isPM && h !== 12) hours += 12;
    if (!isPM && h === 12) hours = 0;
    return hours * 60 + m;
};

const TeacherDashboard = () => {
    const { catchResponse } = useCatchResponse();
    const [currentDay, setCurrentDay] = useState(getTodayDay);
    const [teacherName, setTeacherName] = useState('');
    const [timetableData, setTimetableData] = useState({});
    const [statsData, setStatsData] = useState({ totalClasses: 0, totalPeriods: 0, freePeriods: 0, totalStudents: 0, assignedClassNames: [] });
    const [attendanceSummary, setAttendanceSummary] = useState({ present: 0, absent: 0, late: 0, total: 0 });

    const greeting = useMemo(() => getGreeting(), []);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setTeacherName(loginData?.Name || 'Teacher');
        getTeacherDashboardData(loginData);
    }, []);

    const getTeacherDashboardData = async (loginData) => {
        try {
            const selectedYear = CommonConfig.getCookie('selectedAcademicYear');
            const data = {
                TeacherId: loginData?._id,
                FinancialYearId: selectedYear,
                UserId: loginData?.CreatedBy || loginData?._id,
            };
            Loader.show();
            await api.post(APIConstant.path.GetTeacherDashboardData, data).then(response => {
                if (response.success) {
                    setTimetableData(response.data?.timetable || {});
                    setStatsData(response.data?.stats || { totalClasses: 0, totalPeriods: 0, freePeriods: 0, totalStudents: 0 });
                    setAttendanceSummary(response.data?.attendanceSummary || { present: 0, absent: 0, late: 0, total: 0 });
                }
                Loader.hide();
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            Loader.hide();
            console.log(err);
            catchResponse(err);
        }
    };

    const todayDay = dayNames[new Date().getDay()];
    const todaySchedule = timetableData[currentDay] || [];
    const realTodaySchedule = timetableData[todayDay] || [];

    const currentPeriodCount = useMemo(
        () => realTodaySchedule.filter(p => p.type === 'lecture').length,
        [realTodaySchedule]
    );

    const freePeriodCount = useMemo(
        () => realTodaySchedule.filter(p => p.type === 'free').length,
        [realTodaySchedule]
    );

    const currentPeriodIdx = useMemo(() => {
        if (currentDay !== todayDay) return -1;
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        for (let i = 0; i < todaySchedule.length; i++) {
            const [startStr, endStr] = todaySchedule[i].time.split(' - ');
            const startMin = parseTimeToMinutes(startStr);
            const endMin = parseTimeToMinutes(endStr);
            if (currentTime >= startMin && currentTime <= endMin) return i;
        }
        return -1;
    }, [todaySchedule, currentDay, todayDay]);

    const allTimeSlots = useMemo(() => {
        const timeSlotsSet = new Map();
        days.forEach(day => {
            (timetableData[day] || []).forEach(slot => {
                if (slot.type === 'lecture' && !timeSlotsSet.has(slot.time)) {
                    timeSlotsSet.set(slot.time, parseTimeToMinutes(slot.time.split(' - ')[0]));
                }
            });
        });
        return [...timeSlotsSet.entries()]
            .sort((a, b) => a[1] - b[1])
            .map(([time]) => time);
    }, [timetableData]);

    return (
        <div className="wayment-owner-dashboard et-master-page teacher-dashboard">
            {/* Standard Heading Area */}
            <div className="heading-area p-d-flex p-ai-center p-jc-between p-py-2">
                <div className="p-d-flex p-ai-center p-jc-between p-w-100">
                    <div className="p-d-flex p-ai-center gap-2">
                        <i className="pi pi-desktop"></i>
                        <h2>TEACHER DASHBOARD</h2>
                    </div>
                </div>
            </div>

            {/* Greeting Banner */}
            <div className="td-header">
                <div className="td-header-left">
                    <div className="td-greeting">
                        <h1>{greeting}, <span className="td-teacher-name">{teacherName}</span></h1>
                        <p className="td-date">
                            {new Date().toLocaleDateString('en-US', {
                                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                            })}
                        </p>
                    </div>
                </div>
                <div className="td-header-right">
                    <div className="td-quick-stat">
                        <i className="pi pi-book"></i>
                        <div>
                            <span className="td-stat-value">{currentPeriodCount}</span>
                            <span className="td-stat-label">Classes Today</span>
                        </div>
                    </div>
                    <div className="td-quick-stat">
                        <i className="pi pi-clock"></i>
                        <div>
                            <span className="td-stat-value">{freePeriodCount}</span>
                            <span className="td-stat-label">Free Periods</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="td-stats-row">
                <div className="td-stat-card td-stat-classes td-has-tooltip">
                    <div className="td-stat-icon-wrap">
                        <i className="pi pi-th-large"></i>
                    </div>
                    <div className="td-stat-info">
                        <span className="td-stat-number">{statsData.totalClasses}</span>
                        <span className="td-stat-title">Assigned Classes</span>
                    </div>
                    {statsData.assignedClassNames?.length > 0 && (
                        <div className="td-tooltip">
                            <div className="td-tooltip-title">Assigned Classes</div>
                            {statsData.assignedClassNames.map((name, i) => (
                                <div key={i} className="td-tooltip-item">{name}</div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="td-stat-card td-stat-periods">
                    <div className="td-stat-icon-wrap">
                        <i className="pi pi-calendar"></i>
                    </div>
                    <div className="td-stat-info">
                        <span className="td-stat-number">{statsData.totalPeriods}</span>
                        <span className="td-stat-title">Weekly Periods</span>
                    </div>
                </div>
                <div className="td-stat-card td-stat-free">
                    <div className="td-stat-icon-wrap">
                        <i className="pi pi-sun"></i>
                    </div>
                    <div className="td-stat-info">
                        <span className="td-stat-number">{statsData.freePeriods}</span>
                        <span className="td-stat-title">Free Periods/Week</span>
                    </div>
                </div>
                <div className="td-stat-card td-stat-students">
                    <div className="td-stat-icon-wrap">
                        <i className="pi pi-users"></i>
                    </div>
                    <div className="td-stat-info">
                        <span className="td-stat-number">{statsData.totalStudents}</span>
                        <span className="td-stat-title">Total Students</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="td-main-content">
                {/* Timetable Section */}
                <div className="td-timetable-section">
                    <div className="td-section-header">
                        <h2><i className="pi pi-table"></i> My Timetable</h2>
                    </div>

                    {/* Day Tabs */}
                    <div className="td-day-tabs">
                        {days.map(day => (
                            <button
                                key={day}
                                className={`td-day-tab ${currentDay === day ? 'active' : ''}`}
                                onClick={() => setCurrentDay(day)}
                            >
                                <span className="td-day-short">{day.slice(0, 3)}</span>
                                <span className="td-day-full">{day}</span>
                            </button>
                        ))}
                    </div>

                    {/* Timetable Grid */}
                    <div className="td-timetable-grid">
                        {todaySchedule.length > 0 ? todaySchedule.map((slot, index) => (
                            <div
                                key={index}
                                className={`td-period-card ${slot.type} ${currentPeriodIdx === index ? 'current' : ''}`}
                            >
                                <div className="td-period-number">
                                    <span>{slot.time}</span>
                                    {currentPeriodIdx === index && <span className="td-live-badge">LIVE</span>}
                                </div>
                                {slot.type === 'lecture' ? (
                                    <div className="td-period-details">
                                        <div className="td-subject-name">{slot.subject}</div>
                                        <div className="td-class-info">
                                            <span><i className="pi pi-users"></i> {slot.class}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="td-period-details td-free-period">
                                        <div className="td-free-text">
                                            <i className="pi pi-sun"></i> Free Period
                                        </div>
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="td-no-data">
                                <i className="pi pi-info-circle"></i>
                                <span>No timetable data available</span>
                            </div>
                        )}
                    </div>

                    {/* Weekly Overview Table */}
                    {allTimeSlots.length > 0 && (
                        <div className="td-weekly-overview">
                            <h3><i className="pi pi-calendar-plus"></i> Weekly Overview</h3>
                            <div className="td-weekly-table-wrap">
                                <table className="td-weekly-table">
                                    <thead>
                                        <tr>
                                            <th>Time</th>
                                            {days.map(day => (
                                                <th key={day} className={todayDay === day ? 'active-day' : ''}>
                                                    {day.slice(0, 3)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allTimeSlots.map(timeSlot => (
                                            <tr key={timeSlot}>
                                                <td className="td-period-label">{timeSlot}</td>
                                                {days.map(day => {
                                                    const slot = (timetableData[day] || []).find(s => s.time === timeSlot);
                                                    return (
                                                        <td
                                                            key={day}
                                                            className={`td-weekly-cell ${slot?.type || ''} ${todayDay === day ? 'active-day' : ''}`}
                                                        >
                                                            {slot?.type === 'lecture' ? (
                                                                <div className="td-cell-content">
                                                                    <span className="td-cell-class">{slot.classShort || slot.class}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="td-cell-free">Free</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar */}
                <div className="td-right-sidebar">
                    {/* Attendance Summary */}
                    <div className="td-attendance-card">
                        <div className="td-card-header">
                            <h3><i className="pi pi-check-square"></i> My Attendance (Current Month)</h3>
                        </div>
                        <div className="td-self-attendance">
                            <div className="td-att-summary-item td-att-present-item">
                                <div className="td-att-summary-icon">
                                    <i className="pi pi-check-circle"></i>
                                </div>
                                <div className="td-att-summary-info">
                                    <span className="td-att-summary-count">{attendanceSummary?.present}</span>
                                    <span className="td-att-summary-label">Present</span>
                                </div>
                            </div>
                            <div className="td-att-summary-item td-att-absent-item">
                                <div className="td-att-summary-icon">
                                    <i className="pi pi-times-circle"></i>
                                </div>
                                <div className="td-att-summary-info">
                                    <span className="td-att-summary-count">{attendanceSummary?.absent}</span>
                                    <span className="td-att-summary-label">Absent</span>
                                </div>
                            </div>
                            <div className="td-att-summary-item td-att-late-item">
                                <div className="td-att-summary-icon">
                                    <i className="pi pi-clock"></i>
                                </div>
                                <div className="td-att-summary-info">
                                    <span className="td-att-summary-count">{attendanceSummary?.late}</span>
                                    <span className="td-att-summary-label">Late</span>
                                </div>
                            </div>
                            <div className="td-att-summary-item td-att-holiday-item">
                                <div className="td-att-summary-icon">
                                    <i className="pi pi-calendar"></i>
                                </div>
                                <div className="td-att-summary-info">
                                    <span className="td-att-summary-count">{attendanceSummary?.holidays ?? 0}</span>
                                    <span className="td-att-summary-label">Holidays</span>
                                </div>
                            </div>
                            {attendanceSummary?.total > 0 && (
                                <div className="td-att-percentage">
                                    <div className="td-att-percentage-bar">
                                        <div
                                            className="td-att-percentage-fill"
                                            style={{ width: `${(((attendanceSummary?.present || 0) + (attendanceSummary?.late || 0)) / attendanceSummary?.total) * 100}%` }}
                                        ></div>
                                    </div>
                                    <span className="td-att-percentage-text">
                                        {Math.round((((attendanceSummary?.present || 0) + (attendanceSummary?.late || 0)) / attendanceSummary?.total) * 100)}% Attendance
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeacherDashboard;
